import { useEffect, useState } from 'react';
import { View } from 'react-native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import moment from 'moment';
import { LicenseStatus, LicenseType } from '@/api/services/user';
import { logError } from '@/utils/logHandling';
import { Button } from '@/components/ui/therapist/Button';
import { Icon } from '@/components/Icon';
import { PREPARED_DIALOGS } from '@/components/ui/shared/Dialog/constants';
import { Text } from '@/components/Text';
import { PatientStackParamList } from '@/navigations/PatientStack';
import {
  showDigaPatientWarning,
  showSelfPayerWarning,
  showTrialWarning,
} from '@/utils/licenseCheck';
import { useDialog } from '@/hooks/useDialog';
import { useIsAdditionalContactBannerDismissed } from '@/hooks/useIsAdditionalContactBannerDismissed';
import { useStoreState } from '@/stores/store';

import { styles } from './styles';

export const PatientLicenseBanner = ({
  licenseStatus,
  isLoading,
}: {
  licenseStatus: LicenseStatus | null;
  isLoading: boolean;
}) => {
  const { isDismissed, dismiss } = useIsAdditionalContactBannerDismissed();
  const isFocused = useIsFocused();
  const navigation =
    useNavigation<StackNavigationProp<PatientStackParamList>>();
  const { showDialog, removeDialog } = useDialog();
  const { user } = useStoreState((state) => state.signedInUser);
  const isPatientDemo = useStoreState((state) => state.isPatientDemo);
  const [licenseStatusMessage, setLicenseStatusMessage] = useState<{
    message: string | React.ReactNode;
    type: LicenseType;
  } | null>(null);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    (async () => {
      try {
        if (!licenseStatus) {
          setLicenseStatusMessage(null);

          return;
        }

        if (showTrialWarning(licenseStatus)) {
          if (licenseStatus.daysLeft > 0) {
            setLicenseStatusMessage({
              message: (
                <Text>
                  Ihre Testphase endet in {licenseStatus.daysLeft}{' '}
                  {licenseStatus.daysLeft === 1 ? 'Tag' : 'Tagen'}. Für die
                  weitere Nutzung{' '}
                  <Text
                    onPress={() => navigation.navigate('DigaCodes')}
                    style={styles.linkText}
                  >
                    lösen Sie einen Rezeptcode von Ihrer Krankenkasse ein
                  </Text>{' '}
                  oder{' '}
                  <Text
                    onPress={() =>
                      showDialog(
                        PREPARED_DIALOGS.selectHealthInsuranceDialog(
                          removeDialog,
                        ),
                      )
                    }
                    style={styles.linkText}
                  >
                    buchen Sie eine Lizenz
                  </Text>
                  .
                </Text>
              ),
              type: 'trial',
            });

            return;
          }

          if (licenseStatus.daysLeft === 0) {
            setLicenseStatusMessage({
              message: (
                <Text>
                  Ihre Testphase endet heute. Für die weitere Nutzung{' '}
                  <Text
                    onPress={() => navigation.navigate('DigaCodes')}
                    style={styles.linkText}
                  >
                    lösen Sie einen Rezeptcode von Ihrer Krankenkasse ein
                  </Text>{' '}
                  oder{' '}
                  <Text
                    onPress={() =>
                      showDialog(
                        PREPARED_DIALOGS.selectHealthInsuranceDialog(
                          removeDialog,
                        ),
                      )
                    }
                    style={styles.linkText}
                  >
                    buchen Sie eine Lizenz
                  </Text>
                  .
                </Text>
              ),
              type: 'trial',
            });

            return;
          }

          if (licenseStatus.daysLeft < 0) {
            setLicenseStatusMessage(null);

            return;
          }
        }

        // The license expired today - we don't show the banner
        if (
          licenseStatus &&
          licenseStatus.daysLeft === 0 &&
          moment(licenseStatus.expireAt).isBefore(moment())
        ) {
          return;
        }

        if (licenseStatus && licenseStatus.daysLeft >= 0) {
          const dayText = licenseStatus.daysLeft === 1 ? 'Tag' : 'Tagen';
          const timeText =
            licenseStatus.daysLeft === 0 &&
            moment(licenseStatus.expireAt).isAfter(moment())
              ? 'Heute endet'
              : `In ${licenseStatus.daysLeft} ${dayText} endet`;

          if (showDigaPatientWarning(licenseStatus)) {
            setLicenseStatusMessage({
              message: (
                <Text>
                  {timeText} Ihre Lizenz – jetzt neuen Rezeptcode beantragen und{' '}
                  <Text
                    onPress={() => navigation.navigate('DigaCodes')}
                    style={styles.linkText}
                  >
                    hier vorplanen
                  </Text>
                  .
                </Text>
              ),
              type: 'diga',
            });

            return;
          }

          if (showSelfPayerWarning(licenseStatus)) {
            setLicenseStatusMessage({
              message: (
                <Text>
                  {timeText} Ihre Lizenz. Anschließend können Sie eine neue
                  Lizenz buchen oder bereits jetzt{' '}
                  <Text
                    onPress={() => showDialog(PREPARED_DIALOGS.kontaktDialog())}
                    style={styles.linkText}
                  >
                    bei uns vorplanen
                  </Text>
                  .
                </Text>
              ),
              type: 'selfpay',
            });

            return;
          }
        }

        setLicenseStatusMessage(null);
      } catch (error) {
        logError('Could not get license status', error);
      }
    })();
  }, [isFocused, licenseStatus]);

  if (isLoading) {
    return null;
  }

  return (
    <>
      {licenseStatusMessage?.message ? (
        <View style={styles.licenseBanner}>
          <Icon name="alertTriangle" size={18} style={{ lineHeight: 24 }} />
          <Text style={{ fontSize: 18, maxWidth: '90%' }}>
            {licenseStatusMessage.message}
          </Text>
        </View>
      ) : !isPatientDemo &&
        !(user?.additionalContact && user?.additionalContact?.email) &&
        !isDismissed ? (
        <View style={styles.additionalContactBanner}>
          <Text style={{ fontSize: 18, maxWidth: '70%' }}>
            <Text
              style={styles.linkText}
              onPress={() => navigation.navigate('PatientAccount')}
            >
              Neu: Tragen Sie jetzt im Benutzerkonto eine Vertrauensperson als
              zusätzlichen Kontakt ein.
            </Text>{' '}
            Diese erhält die gleichen E-Mails von uns und kann Sie bei Bedarf
            unterstützen.
          </Text>
          <Button
            label="Nicht mehr anzeigen"
            onPress={dismiss}
            look="tertiary"
          />
        </View>
      ) : null}
    </>
  );
};
